import React, { useState, useEffect, useRef } from 'react';
import io, { Socket } from 'socket.io-client';
import { MessageItem } from './components/MessageItem';
import Header from './components/layout/Header';
import CancelButton from './components/ui/CancelButton';
import { api } from './services/api';
import { MetapromptingSession, MetapromptingMessage } from './types';
import './App.css';
import './components/layout/Header.css';
import './components/ui/CancelButton.css';

function App() {
  const [prompt, setPrompt] = useState('');
  const [session, setSession] = useState<MetapromptingSession | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollTop = useRef<number>(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const cancelledRef = useRef<boolean>(false);

  useEffect(() => {
    // Detectar si estamos en producción basándose en la URL
    const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
    const socketUrl = isProduction ? window.location.origin : 'http://localhost:3001';
    
    console.log('Socket URL:', socketUrl, 'isProduction:', isProduction);
    
    const newSocket = io(socketUrl, { 
      path: '/socket.io', 
      transports: ['polling','websocket'] 
    });
    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  useEffect(() => {
    if (socket && session) {
      socket.emit('join-session', session.id);
      
      const handleNewMessage = (message: MetapromptingMessage) => {
        setSession(prev => {
          if (!prev) return null;
          return {
            ...prev,
            messages: [...prev.messages, message]
          };
        });
      };

      const handleMessageChunk = ({ messageId, chunk }: { messageId: string, chunk: string }) => {
        setSession(prev => {
          if (!prev) return null;
          return {
            ...prev,
            messages: prev.messages.map(msg => 
              msg.id === messageId 
                ? { ...msg, content: msg.content + chunk }
                : msg
            )
          };
        });
      };

      const handleMessageComplete = ({ messageId, content }: { messageId: string, content: string }) => {
        setSession(prev => {
          if (!prev) return null;
          return {
            ...prev,
            messages: prev.messages.map(msg => 
              msg.id === messageId 
                ? { ...msg, content, isStreaming: false }
                : msg
            )
          };
        });
      };

      const handleSessionCancelled = ({ sessionId }: { sessionId: string }) => {
        console.log('Session cancelled:', sessionId);
        setSession(null);
        setIsProcessing(false);
        setIsCancelling(false);
        cancelledRef.current = false; // Limpiar la referencia cuando se confirma la cancelación
      };
      
      socket.on('new-message', handleNewMessage);
      socket.on('message-chunk', handleMessageChunk);
      socket.on('message-complete', handleMessageComplete);
      socket.on('session-cancelled', handleSessionCancelled);
      
      // Cleanup: remover el listener cuando el componente se desmonte o cambie la sesión
      return () => {
        socket.off('new-message', handleNewMessage);
        socket.off('message-chunk', handleMessageChunk);
        socket.off('message-complete', handleMessageComplete);
        socket.off('session-cancelled', handleSessionCancelled);
      };
    }
  }, [socket, session?.id]);

  useEffect(() => {
    if (!userHasScrolledUp) {
      scrollToBottom();
    }
  }, [session?.messages, userHasScrolledUp]);

  // Detectar cuando el usuario hace scroll manual
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px de tolerancia
      
      // Si el usuario hizo scroll hacia arriba desde la última posición
      if (scrollTop < lastScrollTop.current && !isAtBottom) {
        setUserHasScrolledUp(true);
      }
      
      // Si el usuario scrolleó manualmente hasta el final, reactivar auto-scroll
      if (isAtBottom && userHasScrolledUp) {
        setUserHasScrolledUp(false);
      }
      
      lastScrollTop.current = scrollTop;
    };

    const handleWheel = (e: WheelEvent) => {
      // Si el usuario usa la rueda del mouse hacia arriba, detectar intención de scroll manual
      if (e.deltaY < 0) {
        setUserHasScrolledUp(true);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Detectar teclas de navegación que indican scroll manual
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) {
        if (e.key === 'ArrowUp' || e.key === 'PageUp' || e.key === 'Home') {
          setUserHasScrolledUp(true);
        } else if (e.key === 'End') {
          setUserHasScrolledUp(false);
        }
      }
    };

    container.addEventListener('scroll', handleScroll);
    container.addEventListener('wheel', handleWheel);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [userHasScrolledUp]);

  // Cerrar modal con tecla Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showAboutModal) {
        setShowAboutModal(false);
      }
    };

    if (showAboutModal) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevenir scroll del body cuando el modal está abierto
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [showAboutModal]);

  const scrollToBottom = () => {
    if (!userHasScrolledUp) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || isProcessing) return;

    try {
      setIsProcessing(true);
      setIsCancelling(false); // Asegurar que no esté en estado de cancelación
      cancelledRef.current = false; // Reiniciar la referencia de cancelación
      setUserHasScrolledUp(false); // Reiniciar el control de scroll para nueva sesión
      const newSession = await api.createSession(prompt);
      setSession(newSession);
      setPrompt('');
      
      // Ejecutar automáticamente las iteraciones
      await runAllIterations(newSession.id);
    } catch (error) {
      console.error('Error:', error);
      alert('Error al iniciar la sesión de metaprompting');
    } finally {
      setIsProcessing(false);
    }
  };

  const runAllIterations = async (sessionId: string) => {
    const MAX_ITERATIONS = 5;
    
    for (let i = 0; i < MAX_ITERATIONS; i++) {
      try {
        // Verificar si la sesión fue cancelada usando la referencia
        if (cancelledRef.current) {
          console.log('Iterations stopped due to cancellation');
          break;
        }

        const updatedSession = await api.runIteration(sessionId);
        
        // Solo actualizar el estado y las iteraciones, no los mensajes
        // Los mensajes vienen por WebSocket
        setSession(prev => {
          if (!prev) return null;
          return {
            ...prev,
            status: updatedSession.status,
            iterations: updatedSession.iterations,
            currentPrompt: updatedSession.currentPrompt
          };
        });
        
        if (updatedSession.status === 'completed' || updatedSession.status === 'error' || updatedSession.status === 'cancelled') {
          break;
        }
        
        // Esperar un poco entre iteraciones para que se vea el proceso
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error('Error en iteración:', error);
        break;
      }
    }
  };

  const handleCancelSession = async () => {
    if (!session || isCancelling) return;

    try {
      setIsCancelling(true);
      cancelledRef.current = true; // Marcar como cancelado inmediatamente
      await api.cancelSession(session.id);
      // El resto se maneja en el evento WebSocket 'session-cancelled'
    } catch (error) {
      console.error('Error cancelling session:', error);
      setIsCancelling(false);
      cancelledRef.current = false; // Restaurar si falla
    }
  };

  const handleNewSession = () => {
    setSession(null);
    setIsProcessing(false);
    setIsCancelling(false);
    cancelledRef.current = false; // Reiniciar la referencia de cancelación
    setUserHasScrolledUp(false);
  };

  return (
    <div className="app">
      <Header 
        onShowAboutModal={() => setShowAboutModal(true)}
      />

      <div className="container">
        <form onSubmit={handleSubmit} className="prompt-form">
          <div className="form-header">
            <h3>Ingresa tu prompt para mejorar</h3>
            <p>Los modelos de IA dialogarán para optimizar tu prompt</p>
          </div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Ejemplo: Escribe un artículo sobre inteligencia artificial..."
            className="prompt-input"
            rows={2}
            disabled={isProcessing}
          />
          <button 
            type="submit" 
            className="submit-button"
            disabled={!prompt.trim() || isProcessing}
          >
            {isProcessing ? '🔄 Procesando...' : '🚀 Iniciar Mejora de Prompt'}
          </button>
        </form>

        {session && (
          <div className="session-info">
            <div className="session-header">
              <h2>Diálogo de Mejora de Prompt</h2>
              <div className="session-actions">
                {session.status === 'active' && (
                  <CancelButton 
                    onCancel={handleCancelSession}
                    isLoading={isCancelling}
                  />
                )}
                {(session.status === 'completed' || session.status === 'cancelled' || session.status === 'error') && (
                  <button 
                    onClick={handleNewSession}
                    className="new-session-button"
                    title="Iniciar nueva sesión"
                  >
                    🆕 Nueva Sesión
                  </button>
                )}
              </div>
            </div>
            <div className="session-stats">
              <span className="stat-item">
                <strong>Iteración:</strong> {session.iterations}/5
              </span>
              <span className="stat-item">
                <strong>Estado:</strong> {
                  session.status === 'active' ? '🔄 En proceso' :
                  session.status === 'completed' ? '✅ Completado' :
                  session.status === 'cancelled' ? '🚫 Cancelado' :
                  session.status === 'error' ? '❌ Error' : session.status
                }
              </span>
            </div>
          </div>
        )}

        <div className="messages-container" ref={messagesContainerRef}>
          {session?.messages.map((message, index) => (
            <MessageItem key={index} message={message} />
          ))}
          <div ref={messagesEndRef} />
          {userHasScrolledUp && (
            <div className="scroll-indicator">
              <button 
                onClick={() => {
                  setUserHasScrolledUp(false);
                  setTimeout(() => scrollToBottom(), 100);
                }}
                className="scroll-to-bottom-btn"
                title="Volver al final y reactivar auto-scroll"
              >
                ↓ Ir al final
              </button>
            </div>
          )}
        </div>
      </div>
      
      {showAboutModal && (
        <div className="modal-overlay" onClick={() => setShowAboutModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🎯 Acerca de Metaprompting</h2>
              <button 
                onClick={() => setShowAboutModal(false)}
                className="modal-close-btn"
                aria-label="Cerrar modal"
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-intro">
                Metaprompting es una técnica que utiliza dos modelos complementarios para optimizar prompts: 
                un modelo básico que implementa rápidamente mejoras prácticas y un modelo razonador que 
                proporciona análisis crítico detallado.
              </p>
              
              <div className="model-section">
                <h3>🤖 Modelo Básico</h3>
                <div className="model-details">
                  <p><strong>Modelo:</strong> gpt-4o-mini (latest)</p>
                  <p><strong>Rol:</strong> Aplica mejoras prácticas y rápidas basadas en insights del modelo razonador</p>
                  <p><strong>Icono:</strong> 🤖</p>
                </div>
              </div>

              <div className="model-section">
                <h3>🧠 Modelo Razonador</h3>
                <div className="model-details">
                  <p><strong>Modelo:</strong> o3-mini-2025-01-31</p>
                  <p><strong>Rol:</strong> Realiza análisis crítico profundo y genera sugerencias específicas</p>
                  <p><strong>Icono:</strong> 🧠</p>
                </div>
              </div>
              
              <div className="context-section">
                <h3>💡 Contexto adicional</h3>
                <p>
                  Los Modelos de Lenguaje Grande (LLM) son software programado por humanos mediante reglas específicas, 
                  al igual que cualquier software. Por ello, diseñar un prompt ideal requiere un entendimiento profundo 
                  de cómo funcionan estas reglas internas y sus limitaciones.
                </p>
                <p>
                  El Metaprompting busca aprovechar precisamente estos mismos LLM, permitiéndoles iterar y mejorar el 
                  prompt original basándose en las reglas y características propias de su entrenamiento. Los resultados 
                  obtenidos suelen superar significativamente a los generados por prompts escritos directamente por 
                  humanos sin conocimientos específicos sobre el funcionamiento de los LLM.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;